import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('rg:cart-sections', this.#handleEarlySections);
    document.addEventListener('rg:cart-adding', this.#handleEarlyAdd);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('rg:cart-sections', this.#handleEarlySections);
    document.removeEventListener('rg:cart-adding', this.#handleEarlyAdd);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * RG (PJ-4894/#RGR-115): the EMPTY-cart auto-open used to wait for the full
   * event promise (add POST + /cart.json) because the drawer had nothing to
   * show — with the early sections morph (component-cart-items) the content
   * is ready at POST time, so open then. Skips when another modal dialog is
   * up (quick-add) — the promise path's close-deferred open handles that.
   */
  /**
   * The empty→filled morph can intermittently leave `cart-drawer--empty` on
   * the inner wrapper (hydration-mode diff race on the FIRST add), and the
   * empty-state centring then mangles the filled layout. Recompute it from
   * what's actually rendered whenever sections land.
   */
  #syncEmptyClass() {
    // Deferred a macrotask: this component's listeners run BEFORE
    // cart-items-component's morph (parents connect first), so an immediate
    // check would read the pre-morph DOM.
    setTimeout(() => {
      const inner = this.querySelector('.cart-drawer__inner');
      if (!inner) return;
      inner.classList.toggle('cart-drawer--empty', !this.querySelector('.cart-drawer__summary'));
    }, 0);
  }

  /**
   * RG (PJ-4894 round 5): an add that is QUEUED behind an in-flight variant
   * change (product-form.js) has no CartLinesUpdateEvent yet — open with the
   * skeleton straight away so the tap is acknowledged; the queued add's
   * event/sections settle the content.
   */
  #handleEarlyAdd = () => {
    if (!this.hasAttribute('auto-open')) return;
    const otherModal = document.querySelector('dialog[open]:modal');
    if (otherModal && !this.contains(otherModal)) return;
    this.#showSkeleton();
    if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
  };

  #handleEarlySections = () => {
    this.#hideSkeleton();
    this.#syncEmptyClass();
    if (!this.hasAttribute('auto-open') || this.#themeDrawer?.isOpen) return;
    const otherModal = document.querySelector('dialog[open]:modal');
    if (otherModal && !this.contains(otherModal)) return;
    this.#themeDrawer?.open();
  };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #skeletonTimer;

  /**
   * PJ-4894 follow-up: ghost line item from the Add-to-Bag click until the
   * add POST's sections morph in (rg:cart-sections / the event promise —
   * whichever lands first hides it; 8s failsafe covers a dropped request).
   */
  #showSkeleton() {
    const skeleton = this.querySelector('.cart-drawer__skeleton');
    if (!(skeleton instanceof HTMLElement)) return;
    skeleton.hidden = false;
    clearTimeout(this.#skeletonTimer);
    this.#skeletonTimer = setTimeout(() => this.#hideSkeleton(), 8000);
  }

  #hideSkeleton() {
    const skeleton = this.querySelector('.cart-drawer__skeleton');
    if (skeleton instanceof HTMLElement) skeleton.hidden = true;
    clearTimeout(this.#skeletonTimer);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // PJ-4894 follow-up: open at dispatch for EVERY add — empty carts
    // included (the skeleton stands in for content; the empty-state copy
    // hides beneath it via CSS while it shows).
    if (event.action === 'add' && !sourceModal) this.#showSkeleton();
    if (shouldAutoOpen && !sourceModal) {
      this.#themeDrawer?.open();
    }
    // RG (PJ-4894 round 5): quick-add now closes its modal at dispatch (not
    // on the promise), so open with the skeleton the moment that dialog's
    // `close` fires — the old path waited out POST + /cart.json (~3s on
    // staging) before the drawer appeared at all.
    if (shouldAutoOpen && sourceModal?.open) {
      sourceModal.addEventListener(
        'close',
        () => {
          this.#showSkeleton();
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
        },
        { once: true },
      );
    }

    event.promise
      ?.then(({ detail }) => {
        this.#hideSkeleton();
        this.#syncEmptyClass();
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#hideSkeleton();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
